import React from "react"
import { Link, useLocation } from "react-router-dom"
import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"

import MenuIcon from "@mui/icons-material/Menu"
import VpnKeyIcon from "@mui/icons-material/VpnKey"
import LogoutIcon from "@mui/icons-material/Logout"
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts"

import { useSelector, useDispatch } from "react-redux"
import { signOut } from "../reducers/authSlice"
import {
  Avatar,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material"

import NavMenu from "./NavMenu"
import ChangePasswordDialog from "./auth/ChangePasswordDialog"
import { isAdmin, isSuperAdmin } from "../utils/roles"

const user_menu = [
  { label: "Home", link: "/" },
  { label: "Dashboard", link: "/dashboard" },
]

// เมนู admin จัดเป็นกลุ่ม ไม่ให้แถบด้านบนล้น
const admin_menu = [
  ...user_menu,
  {
    label: "Tracking",
    children: [
      { label: "ETA Board", link: "/truckinyard" },
      { label: "Map View", link: "/mapview" },
      { label: "Truck Ready", link: "/truck-ready" },
      { label: "GPS Monitor", link: "/gps-monitor" },
      { label: "ชะอำ", link: "/truck-cha-am" },
    ],
  },
  {
    label: "Reports",
    children: [
      { label: "Driver Rpt", link: "/report_driver" },
      { label: "Warp Rpt", link: "/report_warp" },
      { label: "Traccar", link: "/report_traccar" },
      { label: "Truck Gantt", link: "/truck_gantt" },
    ],
  },
]

const AppNav = () => {
  const user_data = useSelector((state) => state.auth.user)
  const refresh_token = useSelector((state) => state.auth.refreshToken)
  const location = useLocation()

  const dispatch = useDispatch()

  const [anchorElNav, setAnchorElNav] = React.useState(null)
  const [anchorElUser, setAnchorElUser] = React.useState(null)
  const [openChangePassword, setOpenChangePassword] = React.useState(false)

  const display_menu = isAdmin(user_data) ? admin_menu : user_menu

  const handleCloseNavMenu = () => setAnchorElNav(null)
  const handleCloseUserMenu = () => setAnchorElUser(null)

  // เมนูมือถือ: กลุ่มแสดงเป็นหัวข้อ + รายการย่อย
  const mobileItems = display_menu.flatMap((menu) =>
    menu.children
      ? [
          <ListSubheader key={`h-${menu.label}`}>{menu.label}</ListSubheader>,
          ...menu.children.map((child) => (
            <MenuItem
              key={child.link}
              component={Link}
              to={child.link}
              selected={location.pathname === child.link}
              onClick={handleCloseNavMenu}
              sx={{ pl: 4 }}
            >
              {child.label}
            </MenuItem>
          )),
        ]
      : [
          <MenuItem
            key={menu.link}
            component={Link}
            to={menu.link}
            selected={location.pathname === menu.link}
            onClick={handleCloseNavMenu}
          >
            {menu.label}
          </MenuItem>,
        ],
  )

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              color="inherit"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={(e) => setAnchorElNav(e.currentTarget)}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {mobileItems}
            </Menu>
          </Box>
          <Typography
            variant="h6"
            component="div"
            fontWeight="bold"
            noWrap
            sx={{ mr: 3, flexGrow: { xs: 1, md: 0 } }}
          >
            TMS Dashboard
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            <NavMenu menu={display_menu} />
          </Box>

          <IconButton
            onClick={(e) => setAnchorElUser(e.currentTarget)}
            title={user_data.fullname}
            sx={{ p: 0 }}
          >
            <Avatar>{(user_data.fullname || "").substring(0, 1)}</Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorElUser}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography fontWeight="bold">{user_data.fullname}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user_data.username}
              </Typography>
            </Box>
            <Divider />
            {isSuperAdmin(user_data) && (
              <MenuItem
                component={Link}
                to="/users"
                selected={location.pathname === "/users"}
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <ManageAccountsIcon fontSize="small" />
                </ListItemIcon>
                User Management
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleCloseUserMenu()
                setOpenChangePassword(true)
              }}
            >
              <ListItemIcon>
                <VpnKeyIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleCloseUserMenu()
                dispatch(signOut(refresh_token))
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
          <ChangePasswordDialog
            open={openChangePassword}
            onClose={() => setOpenChangePassword(false)}
          />
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default AppNav
